export type IncidentStatus = 'planned' | 'active' | 'stabilizing' | 'recovery' | 'closed' | 'archived';
export type IncidentClassification = 'internal' | 'local' | 'regional' | 'state' | 'federal' | 'cross_jurisdictional';
export type ComplexityLevel = 'Type 1' | 'Type 2' | 'Type 3' | 'Type 4' | 'Type 5';

export interface Incident {
  id: string;
  tenant_id: string;
  incident_number: string;
  name: string;
  short_name?: string;
  type: string;
  status: IncidentStatus;
  classification: IncidentClassification;
  complexity_level: ComplexityLevel;
  description?: string;
  objectives?: string;
  area_of_operations?: GeoJSON.GeoJSON;
  center_lat?: number;
  center_lng?: number;
  jurisdiction_ids: string[];
  parent_incident_id?: string;
  start_time: string;
  end_time?: string;
  created_by?: string;
  closed_by?: string;
  closed_at?: string;
  closed_reason?: string;
  accepts_public_assessments?: boolean;
  accepts_community_reports?: boolean;
  community_reports_public_visible?: boolean;
  operational_period_duration_minutes?: number;
  /** True when this incident is a sandboxed exercise (Exercise Mode). */
  is_exercise?: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface IncidentWithDetails extends Incident {
  created_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  closed_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  current_operational_period?: OperationalPeriod;
  stats?: IncidentStats;
}

export interface CreateIncidentInput {
  name: string;
  short_name?: string;
  type: string;
  classification?: IncidentClassification;
  complexity_level?: ComplexityLevel;
  description?: string;
  objectives?: string;
  start_time: string;
  end_time?: string;
  operational_period_duration_minutes?: number;
}

export interface UpdateIncidentInput {
  name?: string;
  short_name?: string;
  type?: string;
  classification?: IncidentClassification;
  complexity_level?: ComplexityLevel;
  description?: string;
  objectives?: string;
  start_time?: string;
  end_time?: string;
}

export interface PaginatedIncidents {
  data: Incident[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// Lightweight operational period info for dashboard/list views
export interface IncidentCurrentOpPeriod {
  period_number: number;
  start_time: string;
  end_time?: string;
  status: OperationalPeriodStatus;
}

// Incident with current operational period for list views
export interface IncidentWithOpPeriod extends Incident {
  current_op_period?: IncidentCurrentOpPeriod;
}

// Operational period duration presets
export const OPERATIONAL_PERIOD_DURATIONS = [
  { value: 720, label: '12 hours' },
  { value: 1440, label: '24 hours' },
  { value: 2880, label: '48 hours' },
  { value: 4320, label: '72 hours' },
] as const;

// Command Staff (write DTO for the legacy compat endpoint — the data lives on
// the unified incident org tree; read command staff via /incident-org/:id/tree)
export interface CreateCommandStaffInput {
  position: string;
  user_id?: string;
  contact_id?: string;
  external_name?: string;
  external_agency?: string;
  external_contact?: string;
  notes?: string;
}

// ICS Positions
export const ICS_POSITIONS = [
  { value: 'incident_commander', label: 'Incident Commander' },
  { value: 'deputy_incident_commander', label: 'Deputy Incident Commander' },
  { value: 'safety_officer', label: 'Safety Officer' },
  { value: 'public_information_officer', label: 'Public Information Officer' },
  { value: 'liaison_officer', label: 'Liaison Officer' },
  { value: 'operations_section_chief', label: 'Operations Section Chief' },
  { value: 'planning_section_chief', label: 'Planning Section Chief' },
  { value: 'logistics_section_chief', label: 'Logistics Section Chief' },
  { value: 'finance_section_chief', label: 'Finance/Admin Section Chief' },
] as const;

// ICS Position to Qualification Code mapping
export const ICS_POSITION_TO_QUALIFICATION_CODE: Record<string, string> = {
  incident_commander: 'IC',
  deputy_incident_commander: 'DEP_IC',
  safety_officer: 'SO',
  public_information_officer: 'PIO',
  liaison_officer: 'LOFR',
  operations_section_chief: 'OSC',
  planning_section_chief: 'PSC',
  logistics_section_chief: 'LSC',
  finance_section_chief: 'FSC',
};

// Operational Periods
export type OperationalPeriodStatus = 'planned' | 'active' | 'completed';

export interface OperationalPeriod {
  id: string;
  tenant_id: string;
  incident_id: string;
  name: string;
  period_number: number;
  start_time: string;
  end_time?: string;
  objectives?: string;
  weather_forecast?: string;
  safety_message?: string;
  status: OperationalPeriodStatus;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface CreateOperationalPeriodInput {
  name?: string;
  start_time: string;
  end_time?: string;
  objectives?: string;
  weather_forecast?: string;
  safety_message?: string;
}

// User Context
export interface UserContext {
  user_id: string;
  active_incident_id?: string;
  is_daily_ops: boolean;
  updated_at: string;
  active_incident?: Incident;
}

// Incident Statistics
export interface IncidentStats {
  total_tasks: number;
  completed_tasks: number;
  open_tasks: number;
  total_ics_forms: number;
  approved_ics_forms: number;
  total_supply_requests: number;
  total_command_staff: number;
  total_operational_periods: number;
  current_operational_period?: number;
}

// Incident Types
export const INCIDENT_TYPES = [
  { value: 'natural_disaster', label: 'Natural Disaster' },
  { value: 'fire', label: 'Fire' },
  { value: 'hazmat', label: 'Hazardous Materials' },
  { value: 'search_rescue', label: 'Search & Rescue' },
  { value: 'civil_disturbance', label: 'Civil Disturbance' },
  { value: 'public_health', label: 'Public Health' },
  { value: 'planned_event', label: 'Planned Event' },
  { value: 'weather', label: 'Weather' },
  { value: 'flood', label: 'Flood' },
  { value: 'earthquake', label: 'Earthquake' },
  { value: 'hurricane', label: 'Hurricane' },
  { value: 'tornado', label: 'Tornado' },
  { value: 'wildfire', label: 'Wildfire' },
  { value: 'winter_storm', label: 'Winter Storm' },
  { value: 'other', label: 'Other' },
] as const;

// Classifications
export const INCIDENT_CLASSIFICATIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'local', label: 'Local' },
  { value: 'regional', label: 'Regional' },
  { value: 'state', label: 'State' },
  { value: 'federal', label: 'Federal' },
  { value: 'cross_jurisdictional', label: 'Cross-Jurisdictional' },
] as const;

// Complexity Levels
export const COMPLEXITY_LEVELS = [
  { value: 'Type 5', label: 'Type 5 - Local/Initial Response' },
  { value: 'Type 4', label: 'Type 4 - Extended Initial Response' },
  { value: 'Type 3', label: 'Type 3 - Extended Attack' },
  { value: 'Type 2', label: 'Type 2 - Major/Area Command' },
  { value: 'Type 1', label: 'Type 1 - National/Complex' },
] as const;

// Status colors
// Color budget: statuses map onto the severity scale (calm → elevated →
// warning → critical) instead of one arbitrary hue per status. Semantic
// tokens work in both themes.
export const STATUS_COLORS: Record<IncidentStatus, { bg: string; text: string; border: string }> = {
  planned: { bg: 'bg-surface-tertiary', text: 'text-theme-secondary', border: 'border-theme-primary' },
  active: { bg: 'bg-[color:var(--severity-critical-bg)]', text: 'text-[color:var(--severity-critical-text)]', border: 'border-[color:var(--severity-critical-spine)]/30' },
  stabilizing: { bg: 'bg-[color:var(--severity-warning-bg)]', text: 'text-[color:var(--severity-warning-text)]', border: 'border-[color:var(--severity-warning-spine)]/30' },
  recovery: { bg: 'bg-[color:var(--severity-elevated-bg)]', text: 'text-[color:var(--severity-elevated-text)]', border: 'border-[color:var(--severity-elevated-spine)]/30' },
  closed: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-800 dark:text-green-300', border: 'border-green-200 dark:border-green-700' },
  archived: { bg: 'bg-surface-tertiary', text: 'text-theme-secondary', border: 'border-theme-primary' },
};

// Shift Handoff
export interface ShiftHandoffSummaryItem {
  id: string;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  timestamp: string;
  user_name?: string;
}

export interface ShiftHandoffReport {
  incident_id: string;
  incident_name: string;
  since: string;
  generated_at: string;
  tasks: {
    new_count: number;
    completed_count: number;
    overdue_count: number;
    status_changes: number;
    items: ShiftHandoffSummaryItem[];
  };
  supply_requests: {
    new_count: number;
    approved_count: number;
    rejected_count: number;
    fulfilled_count: number;
    items: ShiftHandoffSummaryItem[];
  };
  forms: {
    new_count: number;
    completed_count: number;
    pending_signature_count: number;
    items: ShiftHandoffSummaryItem[];
  };
  personnel: {
    check_in_count: number;
    items: ShiftHandoffSummaryItem[];
  };
  board_posts: {
    new_count: number;
    pinned_count: number;
    items: ShiftHandoffSummaryItem[];
  };
  command_changes: {
    count: number;
    items: ShiftHandoffSummaryItem[];
  };
  incident_status_changes: ShiftHandoffSummaryItem[];
}

// GeoJSON namespace
declare namespace GeoJSON {
  interface GeoJSON {
    type: string;
    coordinates?: unknown;
    features?: unknown[];
    geometry?: unknown;
    properties?: Record<string, unknown>;
  }
}
